import { getSupabaseBrowserClient } from "../integrations/supabase/client";

/**
 * The portal telling the portal that the account's own credentials changed.
 *
 * plan.md section 5.7 makes four of these mandatory in-app and by transactional email, and neither
 * channel may be switched off. They happen inside GoTrue, which owns them and does record them:
 * measured on 2026-09-04, a password change writes `user_updated_password` to
 * `auth.audit_log_entries` and a factor enrolment writes `factor_in_progress`.
 *
 * **No role this project owns may read that table.** `app_auth_owner`, `app_admin_owner` and
 * `service_role` all answer false to `has_table_privilege`, measured the same day. So there is no
 * reconciliation pass to fall back on, and this call is the only producer there is.
 *
 * That makes its failure mode worth stating rather than burying. It is called after GoTrue has
 * already answered, so a browser that closes in between leaves the credential changed and the notice
 * unwritten. Nothing here retries and nothing here tells the person, because the thing they asked
 * for did happen; what did not happen is a record of it. The limitation is named in
 * `docs/pilot-readiness.md` as `PORTAL_SECURITY_NOTICE_DEPENDS_ON_THE_BROWSER`.
 *
 * It never throws. A screen that has just changed somebody's password must not then show them an
 * error about a notification.
 */

export type AccountSecurityEvent =
  | "password_changed"
  | "mfa_enabled"
  | "mfa_replaced"
  | "mfa_disabled";

export async function recordSecurityEvent(event: AccountSecurityEvent): Promise<boolean> {
  try {
    const supabase = getSupabaseBrowserClient();
    const { error } = await supabase.rpc("record_account_security_event", {
      p_event_type: event
    });

    return !error;
  } catch {
    return false;
  }
}
